/* Carpano Kart — multiplayer (v0.3): servidor WebSocket (server/index.js) com fallback P2P.
 * - Com servidor: a sala vive no servidor; se o dono sair, outro piloto vira dono (nada cai).
 *   Ele cai sozinho pro P2P se o servidor não responder. URL: ?ws=wss://… | ?ws=off | KART_WS_URL no build.
 * - P2P (PeerJS, v0.2): quem cria a sala é o HOST (navegador dele), amigos conectam pelo código.
 * - cria sala: peer id = 'carpano-kart-<CÓDIGO>' (código de 4 letras amigável)
 * - entrar: peer.connect('carpano-kart-<CÓDIGO>')
 * - estado: mesmo protocolo do server (join/pos/state), mas o HOST retransmite pra todos.
 * Fallback: se o PeerJS (broker público) não responder, o jogo segue em solo. */
import * as THREE from 'three';
import Peer from 'peerjs';
import * as CANNON from 'cannon-es';
import carCatalog, { DEFAULT_CAR } from '../Cars/CarCatalog.js'
import characterCatalog, { DEFAULT_CHARACTER } from '../Content/CharacterCatalog.js'
import { disposeVisual as disposeChar } from '../Content/CharacterVisual.js';
import { makeProceduralBody, makeStaticWheels, disposeVisual } from '../Cars/CarVisual.js';
import { WsLink } from './WsLink.js';

const REMOTE_UPDATE_HZ = 20;
const LERP_RATE = 10;
const BUILD_WS_URL = process.env.KART_WS_URL || ''; // definido no build (webpack DefinePlugin)

// onde fica o servidor: ?ws=… > build > dev local (http) na porta 8801. '' = só P2P.
function resolveWsUrl() {
    let q = null;
    try { q = new URLSearchParams(location.search).get('ws'); } catch (_) { /* sem location */ }
    if (q === 'off' || q === '0') return '';
    let url = q || BUILD_WS_URL;
    if (!url) {
        try { if (location.protocol === 'http:') url = `ws://${location.hostname}:8801`; } catch (_) { /* ok */ }
    }
    if (url && !/^wss?:\/\//i.test(url)) url = 'wss://' + url;
    return url;
}

function makeCode() {
    const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sem caracteres confusos (I,1,O,0)
    let s = '';
    for (let i = 0; i < 4; i++) s += abc[Math.floor(Math.random() * abc.length)];
    return s;
}

export class KartNet {
    constructor(experience) {
        this.experience = experience;
        this.scene = experience.scene;
        this.peer = null;
        this.isHost = false;
        this.connected = false;
        this.roomCode = null;
        this.conns = new Map(); // peerId → DataConnection
        this.guestIds = new Map(); // connId → { id, name, color }
        this.remotes = new Map(); // playerId → { group, targetP, targetQ, curP, curQ, lastSeen, name, color }
        this.myId = null;
        this.myCar = DEFAULT_CAR
        this.myCharacter = DEFAULT_CHARACTER; // id do personagem/piloto (catálogo)
        this._sendAcc = 0;
        this.onStatus = null; // callback UI (texto do MP)
        this._reconnectT = null;
        this._evN = 0;
        this._seen = new Set(); // eventos já processados (dedupe)
        this._lastBump = 0;
        this.mode = null; // 'ws' | 'p2p'
        this.link = null; // WsLink
        this.token = null; // retoma a vaga no servidor após queda
        this.ownerId = null;
        try {
            // fechar a aba = sai da sala na hora (senão o kart fica 12s "fantasma" pros outros)
            window.addEventListener('pagehide', (e) => { if (!e.persisted) this.link?.send({ t: 'leave' }); });
        } catch (_) { /* sem window */ }
    }

    // id usado pela corrida/itens: no servidor é o id da sala; no P2P o host é sempre 'host'
    selfId() { return this.mode === 'ws' ? this.myId : (this.isHost ? 'host' : this.myId); }

    status(msg) { try { this.onStatus && this.onStatus(msg); } catch (_) {} }

    // ==== criar sala (host) ====
    hostRoom(name, color) {
        this._cleanup();
        this.myName = name || this.myName || 'Host';
        this.myColor = color || this.myColor || 0xFF0000;
        const url = resolveWsUrl();
        if (url) {
            this.isHost = true;
            this._wsStart(url, { t: 'host', name: this.myName, color: this.myColor, car: this.myCar, character: this.myCharacter }, () => this._hostP2P());
        } else this._hostP2P();
    }

    _hostP2P() {
        this.mode = 'p2p';
        this.isHost = true;
        this.roomCode = makeCode();
        this.myId = 'host';
        this._initPeer('carpano-kart-' + this.roomCode, () => {
            this.connected = true;
            this.status(`🏁 Sala ${this.roomCode} criada — manda o código pros amigos!`);
        });
    }

    // ==== entrar em sala (guest) ====
    joinRoom(code, name, color) {
        this._cleanup();
        this.isHost = false;
        this.roomCode = String(code || '').toUpperCase().trim();
        if (!this.roomCode) return;
        this.myName = name || this.myName || 'Piloto';
        this.myColor = color || this.myColor || 0xFF0000;
        const url = resolveWsUrl();
        if (url) this._wsStart(url, { t: 'join', code: this.roomCode, name: this.myName, color: this.myColor, car: this.myCar, character: this.myCharacter }, () => this._joinP2P(name, color));
        else this._joinP2P(name, color);
    }

    _joinP2P(name, color) {
        this.mode = 'p2p';
        this.isHost = false;
        const guestId = 'guest-' + Math.random().toString(36).slice(2, 8);
        this.myId = guestId;
        this._initPeer('carpano-kart-g-' + guestId, () => {
            // conecta no host
            const conn = this.peer.connect('carpano-kart-' + this.roomCode, { reliable: false });
            this._wire(conn, name, color);
            this.status('Conectando na sala ' + this.roomCode + '…');
        });
    }

    // ==== servidor WebSocket ====
    _wsStart(url, firstMsg, fallback) {
        this.mode = 'ws';
        this.status('Conectando ao servidor…');
        this.link = new WsLink(url, {
            onMsg: (m) => this._wsHandle(m),
            onUnreachable: () => {
                this._closeLink();
                this.status('Servidor indisponível — usando conexão direta (P2P)');
                fallback();
            },
            onDrop: () => this.status('⚠️ Conexão perdida — reconectando…'),
            onGiveUp: () => this._wsGiveUp('Conexão perdida — modo solo'),
            makeResume: () => (this.token && this.roomCode
                ? { t: 'join', code: this.roomCode, token: this.token, name: this.myName, color: this.myColor, car: this.myCar, character: this.myCharacter }
                : null),
        });
        this.link.connect(firstMsg);
    }

    _closeLink() {
        if (this.link) { this.link.close(); this.link = null; }
        this.mode = null;
    }

    _wsHandle(m) {
        switch (m.t) {
            case 'joined': this._wsJoined(m); break;
            case 'peer-join': this._upsertRemote({ id: m.id, name: m.name, color: m.color, car: m.car, p: m.p, q: m.q }); break;
            case 'peer-leave': this._removeRemote(m.id); break;
            case 'state': for (const p of m.players) if (p.id !== this.myId) this._upsertRemote(p); break;
            case 'owner': this._setOwner(m.id); break;
            case 'race': this.experience.race?.onNetRace(m); break;
            case 'ev': if (this._firstSeen(m.eid)) this.experience.items?.onNetEvent(m); break;
            case 'error': this._wsError(m.code); break;
            default: break;
        }
    }

    _wsJoined(m) {
        this.myId = m.you;
        this.token = m.token;
        this.roomCode = m.code;
        this.connected = true;
        this._setOwner(m.owner, true);
        // reconcilia: quem saiu enquanto eu estava fora some; quem está na sala aparece
        const seen = new Set();
        for (const p of m.players) { seen.add(p.id); this._upsertRemote(p); }
        for (const id of [...this.remotes.keys()]) if (!seen.has(id)) this._removeRemote(id);
        if (m.resumed) { this.status('✅ Reconectado'); return; }
        const exp = this.experience;
        if (exp && exp.simpleCar) { exp.simpleCar.gridSlot = m.slot || 0; exp.simpleCar.resetPosition(); }
        if (m.race && m.race.state && m.race.state !== 'lobby') exp.race?.onNetRace({ cmd: 'sync', state: m.race.state });
        this.status(this.isHost && !m.players.length
            ? `🏁 Sala ${m.code} criada — manda o código pros amigos!`
            : `Na sala ${m.code}! ${m.players.length + 1} pilotos`);
    }

    _setOwner(id, silent) {
        const was = this.isHost;
        this.ownerId = id;
        this.isHost = id === this.myId;
        if (this.isHost && !was && !silent) this.status('👑 O dono saiu — agora você é o dono da sala');
        this.experience.race?.onOwnerChange?.();
    }

    _wsError(code) {
        if (code === 'not-owner' || code === 'rate-limit' || code === 'bad-request') return;
        const msg = {
            'room-not-found': this.connected || this.token ? 'Sala encerrada — modo solo' : 'Sala não encontrada',
            'room-full': 'Sala cheia (máx. 8 pilotos)',
            'server-full': 'Servidor lotado, tente de novo',
        }[code] || 'Erro do servidor: ' + code;
        this._wsGiveUp(msg);
    }

    _wsGiveUp(msg) {
        this._closeLink();
        for (const id of [...this.remotes.keys()]) this._removeRemote(id);
        this.token = null;
        this.connected = false;
        this.isHost = false;
        this.experience.race?.onOwnerChange?.();
        this.status(msg);
    }

    // dono pede início/reset da corrida; o servidor responde pra TODOS (inclusive o dono)
    wsRace(cmd) { return this.link ? this.link.send({ t: 'race', cmd }) : false; }

    _initPeer(peerId, onOpen) {
        try {
            this.peer = new Peer(peerId, { debug: 0 });
            this.peer.on('open', onOpen);
            this.peer.on('error', (err) => {
                this.status('MP: ' + (err.type === 'peer-unavailable' ? 'sala não encontrada' : err.type));
                this.connected = false;
            });
            if (this.isHost) {
                this.peer.on('connection', (conn) => {
                    this.conns.set(conn.peer, conn);
                    conn.on('data', (m) => this._hostHandle(conn, m));
                    conn.on('close', () => { this._hostRemove(conn.peer); });
                });
            }
        } catch (err) { try { window.__mpErr = String(err && err.stack || err); } catch (_) {} this.status('MP indisponível: ' + String(err && err.message || err)); }
    }

    _wire(conn, name, color) {
        conn.on('open', () => {
            this.conns.set(conn.peer, conn);
            this.connected = true;
            conn.send({ type: 'join', name, color, car: this.myCar, character: this.myCharacter });
            this.status('Conectado! Aguarde os outros…');
        });
        conn.on('data', (m) => this._guestHandle(m));
        conn.on('close', () => {
            this.connected = false;
            this.status('Host saiu — modo solo');
        });
        conn.on('error', () => {});
    }

    // ==== HOST: recebe dos guests e retransmite ====
    _hostHandle(conn, m) {
        if (!m || typeof m !== 'object') return;
        const connId = conn.peer;
        if (m.type === 'join') {
            const g = { id: connId, name: String(m.name || 'Piloto').slice(0, 16), color: Number(m.color) || 0xFF0000, car: String(m.car || DEFAULT_CAR).slice(0, 32), character: String(m.character || DEFAULT_CHARACTER).slice(0, 32) };
            this.guestIds.set(connId, g);
            // HOST TAMBÉM VÊ O NOVO PILOTO na própria cena
            this._upsertRemote({ id: connId, name: g.name, color: g.color, car: g.car, p: g.p || [55, 2, 5 + 2.5], q: [0, 0, 0, 1] });
            // manda pro novo a lista completa (com o host incluso)
            const list = [{ id: 'host', name: this.myName || 'Host', color: this.myColor || 0xFF0000, car: this.myCar, tot: this._lastTot, fin: this._lastFin, p: this._lastP || [55, 2, 5], q: this._lastQ || [0, 0, 0, 1] }];
            for (const [cid, g] of this.guestIds) if (cid !== connId) list.push({ id: cid, name: g.name, color: g.color, car: g.car, p: g.p || [55, 2, 5], q: g.q || [0, 0, 0, 1] });
            const slot = this.guestIds.size; // 1, 2, 3... (host = 0)
            conn.send({ type: 'welcome', id: connId, slot, players: list, race: this.experience.race?.snapshot() });
            g.p = [55, 2, 5 + slot * 2.5];
            // retransmite entrada pros outros
            for (const [pid, c] of this.conns) if (pid !== connId && c.open) c.send({ type: 'peer-join', id: connId, name: this.guestIds.get(connId).name, color: this.guestIds.get(connId).color, car: this.guestIds.get(connId).car, character: this.guestIds.get(connId).character });
        } else if (m.type === 'ev') {
            if (!this._firstSeen(m.eid)) return;
            for (const [pid, c] of this.conns) if (pid !== connId && c.open) c.send(m); // relé pros outros
            this.experience.items?.onNetEvent(m);
        } else if (m.type === 'pos') {
            const g = this.guestIds.get(connId);
            if (g) {
                g.p = m.p; g.q = m.q; g.s = m.s; g.tot = m.tot; g.fin = m.fin;
                // mantém o kart remoto do HOST vivo e atualizado (senão o stale-check de 6s apaga)
                this._upsertRemote({ id: connId, name: g.name, color: g.color, car: g.car, p: m.p, q: m.q, tot: m.tot, fin: m.fin });
            }
            // retransmite pra todos MENOS quem mandou
            const fwd = { type: 'state', players: [
                { id: 'host', name: this.myName || 'Host', color: this.myColor || 0xFF0000, car: this.myCar, tot: this._lastTot, fin: this._lastFin, p: this._lastP || [55, 2, 5], q: this._lastQ || [0, 0, 0, 1], s: this._lastS || 0 },
                ...[...this.guestIds.entries()].map(([cid, gg]) => ({ id: cid, name: gg.name, color: gg.color, car: gg.car, tot: gg.tot, fin: gg.fin, p: gg.p || [55, 2, 5], q: gg.q || [0, 0, 0, 1], s: gg.s || 0 })),
            ] };
            for (const [pid, c] of this.conns) if (pid !== connId && c.open) c.send(fwd);
        }
    }

    _firstSeen(eid) {
        if (!eid) return true;
        if (this._seen.has(eid)) return false;
        this._seen.add(eid);
        if (this._seen.size > 400) this._seen.delete(this._seen.values().next().value);
        return true;
    }

    // evento de jogo (item etc.) pra todos. Canal é não-confiável -> manda 3x; o receptor dedupa por eid.
    /** Escolhe carro local + avisa rede (próximos join/state carregam o id). */
    setMyCar(carId) {
        this.myCar = String(carId || DEFAULT_CAR).slice(0, 32);
        const car = this.experience.simpleCar || this.experience.world?.simpleCar;
        car?.setCar?.(this.myCar, this.myColor);
        return this.myCar;
    }

    setMyCharacter(characterId) {
        this.myCharacter = String(characterId || DEFAULT_CHARACTER).slice(0, 32);
        const car = this.experience.simpleCar || this.experience.world?.simpleCar;
        car?.setCharacter?.(this.myCharacter);
        return this.myCharacter;
    }

    sendEvent(payload) {
        if (!this.connected) return;
        const msg = { type: 'ev', eid: `${this.myId}#${++this._evN}`, ...payload };
        this._seen.add(msg.eid);
        if (this.mode === 'ws') { this.link?.send({ ...payload, t: 'ev', eid: msg.eid }); return; } // TCP: confiável e em ordem
        const go = () => { for (const [, c] of this.conns) if (c.open) c.send(msg); };
        go(); setTimeout(go, 120); setTimeout(go, 320);
    }

    // HOST -> todos os guests (largada, reset da corrida)
    sendRace(msg) {
        if (!this.isHost) return;
        for (const [, c] of this.conns) if (c.open) c.send(msg);
    }

    _hostRemove(connId) {
        this.conns.delete(connId);
        this.guestIds.delete(connId);
        for (const [pid, c] of this.conns) if (c.open) c.send({ type: 'peer-leave', id: connId });
    }

    // ==== GUEST: recebe do host ====
    _guestHandle(m) {
        if (!m || typeof m !== 'object') return;
        if (m.type === 'welcome') {
            this.myId = m.id;
            // posição de largada deste piloto (grid)
            const exp = this.experience;
            if (exp && exp.simpleCar) { exp.simpleCar.gridSlot = m.slot || 1; exp.simpleCar.resetPosition(); }
            for (const p of m.players) if (p.id !== this.myId) this._upsertRemote(p);
            if (m.race) this.experience.race?.onNetRace({ cmd: 'sync', state: m.race.state });
            this.status('Na sala! ' + m.players.length + ' pilotos');
        } else if (m.type === 'state') {
            for (const p of m.players) if (p.id !== this.myId) this._upsertRemote(p);
        } else if (m.type === 'peer-join') {
            this._upsertRemote({ id: m.id, name: m.name, color: m.color, car: m.car, character: m.character, p: [55, 2, 5], q: [0, 0, 0, 1] });
        } else if (m.type === 'peer-leave') {
            this._removeRemote(m.id);
        } else if (m.type === 'race') {
            this.experience.race?.onNetRace(m);
        } else if (m.type === 'ev') {
            if (this._firstSeen(m.eid)) this.experience.items?.onNetEvent(m);
        }
    }

    // ==== karts remotos (igual a v0.1) ====
    _upsertRemote(p) {
        let r = this.remotes.get(p.id);
        if (!r) {
            r = this._makeRemoteKart(p.name, p.color, p.car);
            this.remotes.set(p.id, r);
            this.scene.add(r.group);
        } else if (p.car && p.car !== r.carId) {
            this._setRemoteCar(r, p.car);
        }
        if (p.character && p.character !== r.characterId) {
            this._setRemoteCharacter(r, p.character);
        }
        if (typeof p.tot === 'number' || p.tot === null) r.tot = p.tot;
        if (p.fin !== undefined) r.fin = p.fin;
        if (p.p) r.targetP.set(p.p[0], p.p[1], p.p[2]);
        if (p.q) r.targetQ.set(p.q[0], p.q[1], p.q[2], p.q[3]);
        r.lastSeen = performance.now();
    }

    _makeRemoteKart(name, color, carId) {
        const T = THREE;
        const group = new T.Group();

        // corpo provisório (kart padrão) até o carro do catálogo carregar; rodas fixas
        const body = makeProceduralBody('kart', color);
        group.add(body.group);
        for (const w of makeStaticWheels(color)) group.add(w);

        const c = document.createElement('canvas');
        c.width = 256; c.height = 64;
        const ctx = c.getContext('2d');
        ctx.font = 'bold 34px system-ui, sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(0,0,0,0.85)';
        ctx.strokeText(name, 128, 32);
        ctx.fillStyle = '#' + new T.Color(color).getHexString();
        ctx.fillText(name, 128, 32);
        const tex = new T.CanvasTexture(c);
        const spr = new T.Sprite(new T.SpriteMaterial({ map: tex, depthTest: false, transparent: true }));
        spr.scale.set(3.2, 0.8, 1);
        spr.position.y = 2.4;
        group.add(spr);

        const r = {
            group, name, color, body: body.group, carId: null, dead: false,
            curP: new T.Vector3(55, 2, 5),
            targetP: new T.Vector3(55, 2, 5),
            curQ: new T.Quaternion(),
            targetQ: new T.Quaternion(),
            lastSeen: performance.now(),
        };
        this._setRemoteCar(r, carId || DEFAULT_CAR);
        r.characterId = null;
        // personagem padrão até sync
        this._setRemoteCharacter(r, DEFAULT_CHARACTER);
        r.ghost = this._makeGhost(r);
        return r;
    }

    // corpo físico CINEMÁTICO que representa o kart remoto no NOSSO mundo: nosso kart bate nele
    _makeGhost() {
        const world = this.experience.worldPhysics?.instance;
        if (!world) return null;
        const b = new CANNON.Body({ mass: 0, type: CANNON.Body.KINEMATIC });
        b.addShape(new CANNON.Box(new CANNON.Vec3(1.05, 0.35, 1.65)), new CANNON.Vec3(0, -0.2, 0));
        b.addShape(new CANNON.Box(new CANNON.Vec3(0.75, 0.22, 0.45)), new CANNON.Vec3(0, -0.18, 1.9));
        b.addShape(new CANNON.Box(new CANNON.Vec3(0.55, 0.14, 0.28)), new CANNON.Vec3(0, -0.25, -1.75));
        b.collisionFilterGroup = 2; // só enxerga o chassi do jogador local (grupo 1)
        b.collisionFilterMask = 1;
        b.isGhost = true;
        b.position.set(55, -50, 5); // fora do caminho até a 1ª posição chegar
        world.addBody(b);
        return b;
    }

    // empurrão extra "estilo kart" nas batidas entre karts
    _hookBump(chassis) {
        this._bumpHooked = true;
        chassis.addEventListener('collide', (e) => {
            if (!e.body || !e.body.isGhost) return;
            const now = performance.now();
            if (now - this._lastBump < 250) return;
            this._lastBump = now;
            const d = new CANNON.Vec3();
            chassis.position.vsub(e.body.position, d);
            d.y = 0;
            if (d.length() < 1e-3) return;
            d.normalize();
            d.scale(40, d);
            chassis.applyImpulse(d, new CANNON.Vec3());
            try { this.experience.world?.gameManager?.playSound(180, 0.08); } catch (_) { /* sem áudio */ }
        });
    }


    // troca o piloto remoto
    _setRemoteCharacter(r, characterId) {
        r.characterId = characterId;
        characterCatalog.buildVisual(characterId, r.color).then((vis) => {
            if (r.dead || r.characterId !== characterId) { disposeChar(vis.group); return; }
            if (r.character) {
                r.group.remove(r.character);
                disposeChar(r.character);
            }
            r.character = vis.group;
            r.group.add(vis.group);
        }).catch(() => {});
    }

    // troca o visual de um kart remoto pelo carro do catálogo (id desconhecido = kart padrão)
    _setRemoteCar(r, carId) {
        r.carId = carId;
        carCatalog.buildVisual(carId, r.color).then((vis) => {
            if (r.dead || r.carId !== carId) { disposeVisual(vis.group); return; }
            r.group.remove(r.body);
            disposeVisual(r.body);
            r.body = vis.group;
            r.group.add(vis.group);
        });
    }

    _removeRemote(id) {
        const r = this.remotes.get(id);
        if (!r) return;
        r.dead = true;
        if (r.ghost) { try { this.experience.worldPhysics.instance.removeBody(r.ghost); } catch (_) { /* ok */ } r.ghost = null; }
        this.scene.remove(r.group);
        r.group.traverse(o => { if (o.material) { o.material.map?.dispose?.(); } });
        disposeVisual(r.group);
        this.remotes.delete(id);
    }

    // ==== loop: chamado pela Experience a cada frame ====
    update(dt, localKart) {
        if (!localKart || !localKart.chassisMesh) return;
        if (!this._bumpHooked && localKart.vehicle?.chassisBody) this._hookBump(localKart.vehicle.chassisBody);

        // enviar estado local a 20Hz
        this._sendAcc = (this._sendAcc || 0) + dt;
        if (this._sendAcc >= 1 / REMOTE_UPDATE_HZ && this.connected) {
            this._sendAcc = 0;
            const p = localKart.chassisMesh.position;
            const q = localKart.chassisMesh.quaternion;
            const v = localKart.vehicle?.chassisBody?.velocity;
            const msg = {
                type: 'pos',
                p: [+p.x.toFixed(2), +p.y.toFixed(2), +p.z.toFixed(2)],
                q: [+q.x.toFixed(3), +q.y.toFixed(3), +q.z.toFixed(3), +q.w.toFixed(3)],
                s: v ? Math.hypot(v.x, v.z) : 0,
                ...(this.experience.race?.netState() || {}),
            };
            if (this.mode === 'ws') {
                this.link?.send({ t: 'pos', p: msg.p, q: msg.q, s: msg.s, tot: msg.tot, fin: msg.fin });
            } else if (this.isHost) {
                this._lastP = msg.p; this._lastQ = msg.q; this._lastS = msg.s; this._lastTot = msg.tot; this._lastFin = msg.fin;
                // host não precisa enviar pra si — os guests leem do broadcast dele
            } else {
                for (const [pid, c] of this.conns) if (c.open) c.send(msg);
            }
        }

        // host: retransmite estado do host pros guests periodicamente (o state do host vai junto
        // com o relé dos guests — aqui só quando ninguém mais mandou)
        if (this.mode === 'p2p' && this.isHost && this.connected) {
            this._relayAcc = (this._relayAcc || 0) + dt;
            if (this._relayAcc >= 1 / REMOTE_UPDATE_HZ) {
                this._relayAcc = 0;
                const fwd = { type: 'state', players: [
                    { id: 'host', name: this.myName || 'Host', color: this.myColor || 0xFF0000, car: this.myCar, tot: this._lastTot, fin: this._lastFin, p: this._lastP || [55, 2, 5], q: this._lastQ || [0, 0, 0, 1], s: this._lastS || 0 },
                ] };
                for (const [pid, c] of this.conns) if (c.open) c.send(fwd);
            }
        }

        // interpolar remotos (dt-based)
        const k = 1 - Math.exp(-LERP_RATE * dt);
        for (const [id, r] of this.remotes) {
            if (this.mode !== 'ws' && performance.now() - r.lastSeen > 6000) { this._removeRemote(id); continue; }
            r.curP.lerp(r.targetP, k);
            r.curQ.slerp(r.targetQ, k);
            r.group.position.copy(r.curP);
            r.group.quaternion.copy(r.curQ);
            const g = r.ghost;
            if (g && dt > 0) {
                const dx = r.curP.x - g.position.x, dy = r.curP.y - g.position.y, dz = r.curP.z - g.position.z;
                if (dx * dx + dy * dy + dz * dz > 100) g.velocity.set(0, 0, 0); // teleporte: sem "chicote"
                else g.velocity.set(dx / dt, dy / dt, dz / dt);
                g.position.set(r.curP.x, r.curP.y, r.curP.z);
                g.quaternion.set(r.curQ.x, r.curQ.y, r.curQ.z, r.curQ.w);
            }
        }
    }

    _cleanup() {
        for (const [id] of this.remotes) this._removeRemote(id);
        if (this.link) { this.link.send({ t: 'leave' }); this.link.close(); this.link = null; }
        this.token = null; this.ownerId = null; this.mode = null;
        for (const [pid, c] of this.conns) try { c.close(); } catch (_) {}
        this.conns.clear();
        this.guestIds.clear();
        try { this.peer && this.peer.destroy(); } catch (_) {}
        this.peer = null;
        this.connected = false;
    }

    dispose() { this._cleanup(); }
}
